import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Wrench,
  Truck,
  Calendar,
  ClipboardList,
  Settings,
  ShoppingCart,
  Shield,
  Building2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthContext';
import type { UserRole } from '@/types';

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  allowedRoles?: UserRole[];
  blockedRoles?: UserRole[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    icon: LayoutDashboard,
    blockedRoles: ['ext_vendor'],
  },
  {
    label: 'Repair Requests',
    to: '/repair-requests',
    icon: Wrench,
    blockedRoles: ['accounting', 'ext_vendor'],
  },
  {
    label: 'Equipment',
    to: '/equipment',
    icon: Truck,
    blockedRoles: ['submitter', 'ext_vendor'],
  },
  {
    label: 'PM Tracking',
    to: '/pm-tracking',
    icon: ClipboardList,
    blockedRoles: ['ext_vendor', 'accounting', 'submitter'],
  },
  {
    label: 'PM Admin',
    to: '/pm-admin',
    icon: Settings,
    allowedRoles: ['pm_admin', 'super_admin'],
  },
  {
    label: 'Ordered Parts',
    to: '/ordered-parts',
    icon: ShoppingCart,
    allowedRoles: ['repair_tech', 'accounting', 'super_admin', 'pm_admin'],
  },
  {
    label: 'My Orders',
    to: '/ordered-parts',
    icon: ShoppingCart,
    allowedRoles: ['ext_vendor'],
  },
  {
    label: '3rd Party Repair',
    to: '/third-party-repair',
    icon: Building2,
    allowedRoles: ['repair_tech', 'super_admin', 'pm_admin'],
  },
  {
    label: 'Calendar',
    to: '/calendar',
    icon: Calendar,
    blockedRoles: ['ext_vendor', 'accounting', 'submitter'],
  },
  {
    label: 'Admin',
    to: '/admin',
    icon: Shield,
    allowedRoles: ['super_admin'],
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { currentUser } = useAuth();

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!currentUser) return false;
    const role = currentUser.role;
    if (item.allowedRoles && !item.allowedRoles.includes(role)) return false;
    if (item.blockedRoles && item.blockedRoles.includes(role)) return false;
    return true;
  });

  return (
    <>
      {/* Desktop sidebar (always visible) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-card border-r border-border transition-transform duration-300 ease-in-out',
          'lg:relative lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Logo / Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Wrench className="h-6 w-6 text-primary" />
            <span className="text-lg font-bold text-foreground">FixFlowIQ</span>
          </div>
          {/* Close button (mobile only) */}
          <button
            className="lg:hidden p-1 rounded-md hover:bg-accent text-muted-foreground"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <ul className="space-y-1">
            {visibleItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} FixFlowIQ
          </p>
        </div>
      </aside>
    </>
  );
}
